import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
  X,
  Calendar as CalendarIcon,
  Clock,
  User,
  MapPin,
  BookOpen,
  Folder,
  FolderPlus,
  ArrowLeft,
  CheckSquare,
  Loader2,
  AlertCircle,
  Download,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCalendarByAcademyYearQuery,
  useGetCalendarByIdQuery,
  useUpdateCalendarMutation,
  useAddHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useAddLeaveBalanceMutation,
  useUpdateLeaveBalanceMutation,
  useDeleteLeaveBalanceMutation,
} from "../../slices/calendarApiSlice";
import {
  useGetUserByEmailQuery,
  useGetUsersQuery,
} from "../../slices/userApiSlice";
import { toast } from "sonner";

const Calendar = () => {
  const { year } = useParams();
  const navigate = useNavigate();

  // Validate year parameter - should be a valid 4-digit year
  const isValidYear = (yearStr) => {
    const yearNum = parseInt(yearStr);
    return (
      !isNaN(yearNum) &&
      yearNum >= 2020 &&
      yearNum <= 2030 &&
      yearStr.length === 4
    );
  };

  // If year is not valid, redirect to calendar overview
  if (!isValidYear(year)) {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const roleBasePath = pathSegments[0];
    navigate(`/${roleBasePath}/calendar`);
    return null;
  }
  const calendarYear = parseInt(year) || new Date().getFullYear();

  const [currentDate, setCurrentDate] = useState(
    new Date(calendarYear, new Date().getMonth(), 1),
  );
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [holidays, setHolidays] = useState([]);
  const [activeCalendar, setActiveCalendar] = useState(null);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [viewMode, setViewMode] = useState("month"); // month, week, day
  const [activeTab, setActiveTab] = useState("calendar"); // calendar, holidays, users, configure
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [calendarConfig, setCalendarConfig] = useState({
    casualLeavePerYear: 10,
    earnedLeaveIncrement: 1.25,
    maxOvertimeHours: 40,
    weekendSelection: {
      saturday: true,
      sunday: true,
    },
  });
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({
    casualLeave: 0,
    earnedLeave: 0,
  });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showHolidayDetailsModal, setShowHolidayDetailsModal] = useState(false);
  const [selectedDateHolidays, setSelectedDateHolidays] = useState([]);
  const [selectedDateForDetails, setSelectedDateForDetails] = useState(null);

  // Get user info from Redux
  const userRole = useSelector((state) => state.auth.userRole);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const userEmail = userInfo?.username || userInfo?.email;
  const canEdit = ["cfm", "CFM"].includes(userRole);
  const isCFM = ["cfm", "CFM"].includes(userRole);
  const isAdmin = ["admin", "Admin", "super_admin", "Super Admin"].includes(
    userRole,
  );

  // Get user data from API to get actual academy ID
  const { data: userByEmail } = useGetUserByEmailQuery(userEmail, {
    skip: !userEmail,
  });

  // Get academy ID from API data
  const academyId =
    userByEmail?.user?.academyId || userInfo?.academy_id || userInfo?.academyId;

  // Fetch all users for the calendar
  const { data: allUsers, isLoading: isLoadingUsers } = useGetUsersQuery();

  // Fetch calendar data from API - use different endpoints based on user role
  const {
    data: calendarByAcademyYear,
    isLoading: isLoadingByAcademyYear,
    error: errorByAcademyYear,
    refetch: refetchByAcademyYear,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: academyId,
      year: parseInt(calendarYear),
    },
    {
      skip: isCFM, // Skip this for CFM users
    },
  );

  const {
    data: calendarById,
    isLoading: isLoadingById,
    error: errorById,
    refetch: refetchById,
  } = useGetCalendarByIdQuery(calendarByAcademyYear?.calendar?.id, {
    skip: !isCFM || !calendarByAcademyYear?.calendar?.id, // Only for CFM users and when we have calendar ID
  });

  // For CFM users, we need to first get the calendar by academy/year to get the ID, then get full details
  const {
    data: cfmCalendarByAcademyYear,
    isLoading: isLoadingCfmByAcademyYear,
    error: errorCfmByAcademyYear,
    refetch: refetchCfmByAcademyYear,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: academyId,
      year: parseInt(calendarYear),
    },
    {
      skip: !isCFM, // Only for CFM users
    },
  );

  const {
    data: cfmCalendarById,
    isLoading: isLoadingCfmById,
    error: errorCfmById,
    refetch: refetchCfmById,
  } = useGetCalendarByIdQuery(cfmCalendarByAcademyYear?.calendar?.id, {
    skip: !isCFM || !cfmCalendarByAcademyYear?.calendar?.id, // Only for CFM users and when we have calendar ID
  });

  // Use appropriate data based on user role
  const calendarData = isCFM ? cfmCalendarById : calendarByAcademyYear;
  const isLoading = isCFM
    ? isLoadingCfmByAcademyYear || isLoadingCfmById
    : isLoadingByAcademyYear;
  const error = isCFM
    ? errorCfmByAcademyYear || errorCfmById
    : errorByAcademyYear;
  const refetch = isCFM ? refetchCfmById : refetchByAcademyYear;

  // Update calendar mutation
  const [updateCalendar, { isLoading: isUpdating }] =
    useUpdateCalendarMutation();

  // Holiday management mutations
  const [addHoliday, { isLoading: isAddingHoliday }] = useAddHolidayMutation();
  const [updateHoliday, { isLoading: isUpdatingHoliday }] =
    useUpdateHolidayMutation();
  const [deleteHoliday, { isLoading: isDeletingHoliday }] =
    useDeleteHolidayMutation();

  // Leave balance management mutations
  const [addLeaveBalance, { isLoading: isAddingLeaveBalance }] =
    useAddLeaveBalanceMutation();
  const [updateLeaveBalance, { isLoading: isUpdatingLeaveBalance }] =
    useUpdateLeaveBalanceMutation();
  const [deleteLeaveBalance, { isLoading: isDeletingLeaveBalance }] =
    useDeleteLeaveBalanceMutation();

  const [holidayForm, setHolidayForm] = useState({
    name: "",
    date: "",
  });

  // Initialize calendar for the specific year
  useEffect(() => {
    if (calendarData) {
      const calendar = calendarData.calendar || calendarData;
      const calendarId = calendar._id || calendar.id;

      // Set active calendar from API data
      setActiveCalendar({
        id: calendarId,
        name: calendar.name,
        description: `Official holidays for ${calendarYear}`,
        year: calendar.year,
        color: "#3b82f6",
        isActive: true,
      });

      // Set holidays from API data
      const holidaysData = calendarData.holidays || [];
      if (holidaysData && Array.isArray(holidaysData)) {
        const apiHolidays = holidaysData.map((holiday, index) => ({
          id: holiday._id || holiday.id || index + 1,
          calendarId: calendar._id || calendar.id,
          name: holiday.title || holiday.name,
          description: holiday.description || "",
          date: holiday.date,
          type: holiday.type || "public",
          isRecurring: holiday.isRecurring || true,
          color: holiday.color || "#ef4444",
        }));
        setHolidays(apiHolidays);
      } else {
        setHolidays([]);
      }

      // Set calendar configuration from API data
      setCalendarConfig({
        casualLeavePerYear: 10, // Default value
        earnedLeaveIncrement:
          (calendar.earnedLeaveIncrementalHour ||
            calendar.earned_leave_incremental_hour ||
            8) / 8, // Convert hours to days
        maxOvertimeHours:
          calendar.maxOvertimeHour || calendar.max_overtime_hour || 40,
        weekendSelection: {
          saturday:
            calendar.isSaturdayWeekend !== undefined
              ? calendar.isSaturdayWeekend
              : calendar.is_saturday_weekend || true,
          sunday:
            calendar.isSundayWeekend !== undefined
              ? calendar.isSundayWeekend
              : calendar.is_sunday_weekend || true,
        },
      });
    }

    // Set current date to the calendar year
    setCurrentDate(new Date(calendarYear, new Date().getMonth(), 1));
  }, [calendarData, calendarYear]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  const isSameDay = (date1, date2) => {
    return date1.toDateString() === date2.toDateString();
  };

  const getHolidaysForDate = (date) => {
    // Format date without timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    return holidays.filter(
      (holiday) =>
        holiday.date === dateStr &&
        (!activeCalendar || holiday.calendarId === activeCalendar.id),
    );
  };

  const handleDateClick = (day) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );
    setSelectedDate(newDate);
  };

  const handleCreateHoliday = (clickedDate = null) => {
    if (!canEdit || !activeCalendar) {
      // Show error if calendar is not active
      if (!activeCalendar) {
        toast.warning("No Active Calendar", {
          description:
            "Please wait for the calendar to load or create a calendar first.",
        });
      }
      return;
    }

    // Use the clicked date if provided, otherwise use selectedDate, or fallback to today
    const dateToUse = clickedDate || selectedDate || new Date();

    // Format date without timezone issues
    const year = dateToUse.getFullYear();
    const month = String(dateToUse.getMonth() + 1).padStart(2, "0");
    const day = String(dateToUse.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    setSelectedHoliday(null);
    setHolidayForm({
      name: "",
      date: formattedDate,
    });
    setShowHolidayModal(true);
  };

  const handleBackToOverview = () => {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const roleBasePath = pathSegments[0];
    navigate(`/${roleBasePath}/calendar`);
  };

  const handleEditHoliday = (holiday) => {
    if (!canEdit) return;

    setSelectedHoliday(holiday);
    setHolidayForm({
      name: holiday.name,
      date: holiday.date,
    });
    setShowHolidayModal(true);
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!canEdit || !calendarData) return;

    try {
      // Show confirmation dialog
      const holiday = holidays.find((h) => h.id === holidayId);

      toast("Delete Holiday", {
        description: "Are you sure you want to delete this holiday?",
        action: {
          label: "Yes, delete it!",
          onClick: async () => {
            try {
              // Use the new delete holiday API endpoint
              const calendarId =
                calendarData?.calendar?.id ||
                calendarData?.id ||
                calendarData?._id;
              await deleteHoliday({
                calendarId: calendarId,
                holidayId: holidayId,
              }).unwrap();

              // Remove holiday from local state
              const updatedHolidays = holidays.filter(
                (holiday) => holiday.id !== holidayId,
              );
              setHolidays(updatedHolidays);

              toast.success("Holiday has been deleted successfully.");
            } catch (error) {
              console.error("Error deleting holiday:", error);
              toast.error("Error!", {
                description:
                  error?.data?.message ||
                  "Failed to delete holiday. Please try again.",
              });
            }
          },
        },
      });
    } catch (error) {
      // This catch block is less likely to be reached now that logic is inside toast callback, but good to keep structure
      console.error("Error setting up delete toast:", error);
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!holidayForm.name.trim()) {
      errors.name = "Holiday name is required";
    } else if (holidayForm.name.trim().length < 2) {
      errors.name = "Holiday name must be at least 2 characters";
    }

    if (!holidayForm.date) {
      errors.date = "Date is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessModal(true);
  };

  const handleEditUser = (userId) => {
    if (!canEdit || !allUsers) return;

    const user = allUsers.find((u) => (u.userId || u.id) === userId);

    if (user) {
      // Get current leave balance from calendar data
      const currentBalance = (
        calendarData?.leave_balances ||
        calendarData?.leave_balance ||
        []
      ).find((balance) => balance.employee_id === user.employeeId);

      setSelectedUser(user);
      setUserEditForm({
        casualLeave: currentBalance?.casual_leave_days || 0,
        earnedLeave: currentBalance?.earned_leave_days || 0,
      });
      setShowUserEditModal(true);
    }
  };

  const handleSaveUserEdit = async () => {
    if (!selectedUser || !calendarData) return;

    try {
      const leaveBalanceData = {
        employee_id: selectedUser.employeeId,
        casual_leave_days: userEditForm.casualLeave,
        earned_leave_days: userEditForm.earnedLeave,
      };

      // Check if user already has leave balance in calendar
      const existingBalance = (
        calendarData?.leave_balances ||
        calendarData?.leave_balance ||
        []
      ).find((balance) => balance.employee_id === selectedUser.employeeId);

      const calendarId =
        calendarData?.calendar?.id || calendarData?.id || calendarData?._id;

      if (existingBalance) {
        // Update existing leave balance using new API endpoint
        await updateLeaveBalance({
          calendarId: calendarId,
          leaveBalanceId: existingBalance._id || existingBalance.id,
          leaveBalanceData: leaveBalanceData,
        }).unwrap();
      } else {
        // Add new leave balance using new API endpoint
        await addLeaveBalance({
          calendarId: calendarId,
          leaveBalanceData: leaveBalanceData,
        }).unwrap();
      }

      setShowUserEditModal(false);
      setSelectedUser(null);

      toast.success("User leave balance updated successfully!");
    } catch (error) {
      console.error("Error updating user leave balance:", error);
      toast.error("Error!", {
        description:
          error?.data?.message ||
          "Failed to update leave balance. Please try again.",
      });
    }
  };

  const handleCancelUserEdit = () => {
    setShowUserEditModal(false);
    setSelectedUser(null);
    setUserEditForm({ casualLeave: 0, earnedLeave: 0 });
  };

  const handleDeleteUserFromCalendar = async (userId) => {
    if (!canEdit || !calendarData) return;

    const user = allUsers.find((u) => (u.userId || u.id) === userId);
    if (!user) return;

    try {
      toast("Remove User", {
        description: `Are you sure you want to remove ${user.name} from this calendar?`,
        action: {
          label: "Yes, remove!",
          onClick: async () => {
            try {
              const calendarId =
                calendarData?.calendar?.id ||
                calendarData?.id ||
                calendarData?._id;
              const existingBalance = (
                calendarData?.leave_balances ||
                calendarData?.leave_balance ||
                []
              ).find((balance) => balance.employee_id === user.employeeId);

              if (existingBalance) {
                await deleteLeaveBalance({
                  calendarId: calendarId,
                  leaveBalanceId: existingBalance._id || existingBalance.id,
                }).unwrap();
              }

              toast.success(`${user.name} has been removed from the calendar.`);
            } catch (error) {
              console.error("Error removing user from calendar:", error);
              toast.error("Error!", {
                description:
                  error?.data?.message ||
                  "Failed to remove user. Please try again.",
              });
            }
          },
        },
      });
    } catch (error) {
      console.error("Error removing user from calendar:", error);
    }
  };

  const handleAddUserToCalendar = async (user) => {
    if (!calendarData) return;

    try {
      const leaveBalanceData = {
        employee_id: user.employeeId,
        casual_leave_days: calendarConfig.casualLeavePerYear, // Use default casual leave from config
        earned_leave_days: 0, // Default earned leave is 0
      };

      const calendarId =
        calendarData?.calendar?.id || calendarData?.id || calendarData?._id;
      await addLeaveBalance({
        calendarId: calendarId,
        leaveBalanceData: leaveBalanceData,
      }).unwrap();

      setShowAddUserModal(false);
      toast.success(
        `${user.name} has been added to the calendar with default leave balance.`,
      );
    } catch (error) {
      console.error("Error adding user to calendar:", error);
      toast.error("Error!", {
        description:
          error?.data?.message ||
          "Failed to add user to calendar. Please try again.",
      });
    }
  };

  const handleOpenAddUserModal = () => {
    if (!allUsers || !calendarData) return;

    // Get users already in the calendar
    const usersInCalendar = (
      calendarData?.leave_balances ||
      calendarData?.leave_balance ||
      []
    ).map((balance) => balance.employee_id);

    // Filter available users (same academy, not admin, not already in calendar)
    const available = allUsers.filter((user) => {
      const userAcademyId = user.academyId || user.academy_id;
      const isSameAcademy = userAcademyId === academyId;
      const userRole =
        user.role?.name || user.userRole?.name || user.role || user.userRole;
      const isNotAdmin = ![
        "admin",
        "Admin",
        "super_admin",
        "Super Admin",
        "superadmin",
        "SUPERADMIN",
        "ADMIN",
      ].includes(userRole);
      const isNotInCalendar = !usersInCalendar.includes(user.employeeId);

      return isSameAcademy && isNotAdmin && isNotInCalendar;
    });

    setAvailableUsers(available);
    setShowAddUserModal(true);
  };

  const handleExportHolidays = () => {
    if (!holidays || holidays.length === 0) {
      toast.warning("No Holidays", {
        description: "There are no holidays to export.",
      });
      return;
    }

    try {
      // Sort holidays by date
      const sortedHolidays = [...holidays].sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });

      // Format data for Excel - date as d/m (day/month) without year
      const excelData = sortedHolidays.map((holiday) => {
        const date = new Date(holiday.date);
        // Format as "d/m" (e.g., "15/1", "25/12")
        const day = date.getDate();
        const month = date.getMonth() + 1; // getMonth() returns 0-11
        const formattedDate = `${day}/${month}`;

        return {
          date: formattedDate,
          holiday_name: holiday.name,
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Holidays");

      // Set column widths
      worksheet["!cols"] = [
        { wch: 10 }, // Date column
        { wch: 30 }, // Holiday Name column
      ];

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      // Create blob and download
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `Holidays_${calendarYear}.xlsx`;
      saveAs(blob, fileName);

      toast.success("Exported!", {
        description: `Holidays for ${calendarYear} have been exported successfully.`,
      });
    } catch (error) {
      console.error("Error exporting holidays:", error);
      toast.error("Export Failed", {
        description: "Failed to export holidays. Please try again.",
      });
    }
  };

  // Check if form is valid for submit button
  const isFormValid = () => {
    return holidayForm.name.trim().length >= 2 && holidayForm.date;
  };

  const handleSaveHoliday = async () => {
    if (!validateForm() || !calendarData) {
      return;
    }

    try {
      const holidayData = {
        date: holidayForm.date,
        title: holidayForm.name,
      };

      if (selectedHoliday) {
        // Update existing holiday using new API endpoint
        const calendarId =
          calendarData?.calendar?.id || calendarData?.id || calendarData?._id;
        await updateHoliday({
          calendarId: calendarId,
          holidayId: selectedHoliday.id,
          holidayData: holidayData,
        }).unwrap();

        // Update local state
        const updatedHolidays = holidays.map((holiday) =>
          holiday.id === selectedHoliday.id
            ? {
              ...holiday,
              name: holidayForm.name,
              date: holidayForm.date,
            }
            : holiday,
        );
        setHolidays(updatedHolidays);
      } else {
        // Create new holiday using new API endpoint
        const calendarId =
          calendarData?.calendar?.id || calendarData?.id || calendarData?._id;
        const result = await addHoliday({
          calendarId: calendarId,
          holidayData: holidayData,
        }).unwrap();

        // Add new holiday to local state
        const newHoliday = {
          id: result._id || result.id || Date.now(),
          name: holidayForm.name,
          date: holidayForm.date,
          calendarId: calendarData._id || calendarData.id,
        };
        setHolidays([...holidays, newHoliday]);
      }

      setShowHolidayModal(false);
      setFormErrors({});
      setHolidayForm({
        name: "",
        date: "",
      });

      toast.success(
        selectedHoliday
          ? "Holiday updated successfully!"
          : "Holiday added successfully!",
      );
    } catch (error) {
      console.error("Error saving holiday:", error);
      toast.error("Error!", {
        description:
          error?.data?.message || "Failed to save holiday. Please try again.",
      });
    }
  };

  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for previous month days
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="aspect-square sm:aspect-auto sm:h-16 md:h-24 lg:h-32"
        ></div>,
      );
    }

    // Days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      const dayHolidays = getHolidaysForDate(date);
      const isSelected = isSameDay(date, selectedDate);
      const isToday = isSameDay(date, new Date());

      days.push(
        <div
          key={day}
          className="aspect-square sm:aspect-auto sm:h-16 md:h-24 lg:h-32 border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-1.5 sm:p-2 flex flex-col"
          onClick={() => {
            const clickedDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              day,
            );
            setSelectedDate(clickedDate);
            if (canEdit) {
              handleCreateHoliday(clickedDate);
            } else if (dayHolidays.length > 0) {
              // For non-CFM users, show holidays if any exist
              setSelectedDateHolidays(dayHolidays);
              setSelectedDateForDetails(clickedDate);
              setShowHolidayDetailsModal(true);
            }
          }}
        >
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-xs sm:text-sm font-medium ${isToday
                  ? "bg-red-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center"
                  : "text-gray-900"
                }`}
            >
              {day}
            </span>
          </div>

          {/* Holidays for this day */}
          <div className="space-y-1 mt-auto">
            {/* Dots for mobile (below sm) */}
            {dayHolidays.length > 0 && (
              <div className="flex items-center justify-center gap-1 flex-wrap sm:hidden">
                {dayHolidays.slice(0, 3).map((holiday) => (
                  <div
                    key={holiday.id}
                    className="w-1.5 h-1.5 bg-red-500 rounded-full cursor-pointer hover:bg-red-600 transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canEdit) {
                        handleEditHoliday(holiday);
                      } else {
                        // For non-CFM users, show holidays in a modal
                        setSelectedDateHolidays(dayHolidays);
                        setSelectedDateForDetails(date);
                        setShowHolidayDetailsModal(true);
                      }
                    }}
                    title={holiday.name}
                  />
                ))}
                {dayHolidays.length > 3 && (
                  <div
                    className="w-1.5 h-1.5 bg-red-300 rounded-full cursor-pointer hover:bg-red-400 transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canEdit) {
                        setSelectedDateHolidays(dayHolidays);
                        setSelectedDateForDetails(date);
                        setShowHolidayDetailsModal(true);
                      }
                    }}
                    title={`+${dayHolidays.length - 3} more holidays`}
                  />
                )}
              </div>
            )}

            {/* Text for sm and above */}
            <div className="hidden sm:block space-y-1">
              {dayHolidays.slice(0, 2).map((holiday) => (
                <div
                  key={holiday.id}
                  className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-100 text-red-800 rounded cursor-pointer hover:bg-red-200 transition-colors duration-200 w-full overflow-hidden"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canEdit) {
                      handleEditHoliday(holiday);
                    } else {
                      // For non-CFM users, show holidays in a modal
                      setSelectedDateHolidays(dayHolidays);
                      setSelectedDateForDetails(date);
                      setShowHolidayDetailsModal(true);
                    }
                  }}
                >
                  <span className="block truncate">{holiday.name}</span>
                </div>
              ))}
              {dayHolidays.length > 2 && (
                <div
                  className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 font-medium cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canEdit) {
                      setSelectedDateHolidays(dayHolidays);
                      setSelectedDateForDetails(date);
                      setShowHolidayDetailsModal(true);
                    }
                  }}
                >
                  +{dayHolidays.length - 2} more
                </div>
              )}
            </div>
          </div>
        </div>,
      );
    }

    return days;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Loading calendar...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error Loading Calendar
          </h3>
          <p className="text-gray-500 mb-4">
            {error?.data?.message ||
              "Failed to load calendar. Please try again."}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show no calendar found state
  if (!calendarData) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Calendar Found
          </h3>
          <p className="text-gray-500 mb-6">
            No calendar found for {calendarYear}. Please create a calendar
            first.
          </p>
          <button
            onClick={handleBackToOverview}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            Back to Calendars
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between py-3 sm:py-4 pb-2 px-3 sm:px-5">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleBackToOverview}
            className="flex items-center p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Back to Calendar Overview"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
            {calendarYear}
          </h1>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3">
          {canEdit && (
            <button
              onClick={() => {
                // Use today's date when button is clicked
                const today = new Date();
                handleCreateHoliday(today);
              }}
              className="flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-black text-white rounded hover:bg-zinc-700 transition-colors duration-200 text-xs sm:text-sm"
              disabled={!activeCalendar}
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Add Holiday</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-4 sm:space-x-6 md:space-x-8 px-3 sm:px-4 md:px-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab("calendar")}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 ${activeTab === "calendar"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Calendar
          </button>
          <button
            onClick={() => setActiveTab("holidays")}
            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 ${activeTab === "holidays"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Holidays
          </button>
          {isCFM && (
            <button
              onClick={() => setActiveTab("users")}
              className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 ${activeTab === "users"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Users
            </button>
          )}
          {isCFM && (
            <button
              onClick={() => setActiveTab("configure")}
              className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 ${activeTab === "configure"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Configure
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-2 sm:p-6">
        {activeTab === "calendar" && (
          <>
            {/* Calendar Header with Zinc Background */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-t-lg mb-0">
              <div className="flex items-center justify-between p-2 sm:p-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-1.5 sm:p-2 rounded-lg hover:bg-zinc-100 transition-colors duration-200"
                  >
                    <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-600" />
                  </button>

                  <h2 className="text-sm sm:text-base md:text-lg font-medium text-zinc-800 min-w-[140px] sm:min-w-[180px] text-center">
                    {formatDate(currentDate)}
                  </h2>

                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-1.5 sm:p-2 rounded-lg hover:bg-zinc-100 transition-colors duration-200"
                  >
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-600" />
                  </button>
                </div>

                <button
                  onClick={() => {
                    const today = new Date();
                    if (today.getFullYear() === parseInt(calendarYear)) {
                      setCurrentDate(today);
                    } else {
                      // Navigate to current year if we're viewing a different year
                      const role =
                        userInfo?.role || userRole?.toLowerCase() || "admin";
                      navigate(`/${role}/calendar/${today.getFullYear()}`);
                    }
                  }}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-zinc-700 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors duration-200"
                >
                  Today
                </button>
              </div>
            </div>

            {/* Days of Week Header */}
            <div className="grid grid-cols-7 gap-0 border-l border-r border-gray-200">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="p-2 sm:p-3 text-center text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wide border-r border-gray-200 last:border-r-0"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-0 border border-gray-200 rounded-b-lg overflow-hidden">
              {renderCalendarGrid()}
            </div>
          </>
        )}

        {activeTab === "holidays" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Holidays for {calendarYear}
              </h3>
              <button
                onClick={handleExportHolidays}
                disabled={!holidays || holidays.length === 0}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-colors duration-200 text-xs sm:text-sm ${!holidays || holidays.length === 0
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-800"
                  }`}
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Export to Excel</span>
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {holidays.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Holidays
                  </h3>
                  <p className="text-gray-500">
                    No holidays have been added for {calendarYear} yet.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {holidays.map((holiday) => (
                    <div
                      key={holiday.id}
                      className="p-4 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 rounded-full bg-gray-400" />
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                              {holiday.name}
                            </h4>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-4">
                          <span className="text-xs sm:text-sm font-medium text-gray-900">
                            {new Date(holiday.date).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </span>
                          {canEdit && (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditHoliday(holiday)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteHoliday(holiday.id)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "users" && isCFM && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {isCFM
                  ? `User Leave Balances for ${calendarYear}`
                  : `My Leave Balance for ${calendarYear}`}
              </h3>
              {isCFM && (
                <button
                  onClick={handleOpenAddUserModal}
                  className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add User</span>
                </button>
              )}
            </div>

            {/* Search Bar - Only for CFM */}
            {isCFM && (
              <div>
                <input
                  type="text"
                  placeholder="Search users by name, email, or employee ID..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-96 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                />
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Profile
                      </th>
                      {isCFM ? (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Details
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Employee ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Casual Leave
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Earned Leave
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Casual Leave
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Earned Leave
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoadingUsers ? (
                      <tr>
                        <td
                          colSpan={canEdit ? 6 : 3}
                          className="px-6 py-8 text-center"
                        >
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">Loading users...</p>
                        </td>
                      </tr>
                    ) : !allUsers ? (
                      <tr>
                        <td
                          colSpan={canEdit ? 6 : 3}
                          className="px-6 py-8 text-center"
                        >
                          <p className="text-gray-500">
                            No user data available
                          </p>
                        </td>
                      </tr>
                    ) : allUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={canEdit ? 6 : 3}
                          className="px-6 py-8 text-center"
                        >
                          <p className="text-gray-500">No users found</p>
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        // Get users already in the calendar with their leave balance data
                        const usersInCalendar =
                          calendarData?.leave_balances ||
                          calendarData?.leave_balance ||
                          [];

                        // Filter users: only show users that are already in the calendar
                        let filteredUsers = allUsers.filter((user) => {
                          return usersInCalendar.some(
                            (balance) =>
                              balance.employee_id === user.employeeId,
                          );
                        });

                        // Apply search filter
                        const searchFiltered = filteredUsers.filter(
                          (user) =>
                            user.name
                              ?.toLowerCase()
                              .includes(userSearchTerm.toLowerCase()) ||
                            user.email
                              ?.toLowerCase()
                              .includes(userSearchTerm.toLowerCase()) ||
                            user.employeeId
                              ?.toLowerCase()
                              .includes(userSearchTerm.toLowerCase()),
                        );

                        return searchFiltered.map((user) => (
                          <tr
                            key={user.userId || user.id}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 relative">
                                  {user.image ? (
                                    <img
                                      className="h-10 w-10 rounded-full"
                                      src={user.image}
                                      alt={user.name}
                                    />
                                  ) : (
                                    <div className="h-10 w-10 rounded-full bg-zinc-200 flex items-center justify-center">
                                      <span className="text-sm font-medium text-zinc-600">
                                        {user.name
                                          ?.split(" ")
                                          .map((n) => n[0])
                                          .join("")
                                          .toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                  {/* "Me" tag for current user */}
                                  {(user.userId || user.id) ===
                                    (userInfo?.userId || userInfo?.id) && (
                                      <span className="absolute -top-1 -right-3 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                                        Me
                                      </span>
                                    )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {user.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {user.email}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.employeeId}
                            </td>
                            {canEdit && (
                              <>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {usersInCalendar.find(
                                    (balance) =>
                                      balance.employee_id === user.employeeId,
                                  )?.casual_leave_days || 0}{" "}
                                  days
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {usersInCalendar.find(
                                    (balance) =>
                                      balance.employee_id === user.employeeId,
                                  )?.earned_leave_days || 0}{" "}
                                  days
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() =>
                                        handleEditUser(user.userId || user.id)
                                      }
                                      className="px-3 py-1 bg-blue-100 text-blue-800 font-semibold rounded hover:bg-blue-200 transition-colors duration-200"
                                    >
                                      Edit
                                    </button>
                                    {/* Only show remove button if not current user */}
                                    {(user.userId || user.id) !==
                                      (userInfo?.userId || userInfo?.id) && (
                                        <button
                                          onClick={() =>
                                            handleDeleteUserFromCalendar(
                                              user.userId || user.id,
                                            )
                                          }
                                          className="px-3 py-1 bg-red-100 text-red-800 font-semibold rounded hover:bg-red-200 transition-colors duration-200"
                                        >
                                          Remove
                                        </button>
                                      )}
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ));
                      })()
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "configure" && isCFM && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Calendar Configuration for {calendarYear}
            </h3>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">
                Leave and Overtime Configuration
              </h4>

              <div className="space-y-6">
                {/* Leave Configuration - Two columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Casual Leave per Year (days)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={calendarConfig.casualLeavePerYear}
                        className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                        min="0"
                        readOnly
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          Read Only
                        </span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Casual leave cannot be changed after calendar creation
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Earned Leave Increment (days/month)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={calendarConfig.earnedLeaveIncrement}
                      onChange={(e) =>
                        setCalendarConfig({
                          ...calendarConfig,
                          earnedLeaveIncrement: parseFloat(e.target.value),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                      min="0"
                    />
                  </div>
                </div>

                {/* Overtime Configuration - Single column */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Overtime Hours per Month
                  </label>
                  <input
                    type="number"
                    value={calendarConfig.maxOvertimeHours}
                    onChange={(e) =>
                      setCalendarConfig({
                        ...calendarConfig,
                        maxOvertimeHours: parseInt(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                    min="0"
                  />
                </div>

                {/* Weekend Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Weekend Selection
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={calendarConfig.weekendSelection.saturday}
                        onChange={(e) => {
                          setCalendarConfig({
                            ...calendarConfig,
                            weekendSelection: {
                              ...calendarConfig.weekendSelection,
                              saturday: e.target.checked,
                            },
                          });
                        }}
                        className="w-4 h-4 text-black border-gray-300 focus:ring-black rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Saturday
                      </span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={calendarConfig.weekendSelection.sunday}
                        onChange={(e) => {
                          setCalendarConfig({
                            ...calendarConfig,
                            weekendSelection: {
                              ...calendarConfig.weekendSelection,
                              sunday: e.target.checked,
                            },
                          });
                        }}
                        className="w-4 h-4 text-black border-gray-300 focus:ring-black rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Sunday
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={async () => {
                    try {
                      const calendarId =
                        calendarData?.calendar?.id ||
                        calendarData?.id ||
                        calendarData?._id;
                      const calendar = calendarData?.calendar || calendarData;

                      // Use the regular updateCalendar mutation instead of metadata-specific endpoint
                      await updateCalendar({
                        id: calendarId,
                        name: calendar.name,
                        year: calendar.year,
                        academy_id: calendar.academyId || calendar.academy_id,
                        is_saturday_weekend:
                          calendarConfig.weekendSelection.saturday,
                        is_sunday_weekend:
                          calendarConfig.weekendSelection.sunday,
                        earned_leave_incremental_hour:
                          calendarConfig.earnedLeaveIncrement * 8, // Convert days to hours
                        max_overtime_hour: calendarConfig.maxOvertimeHours,
                        holidays: calendarData?.holidays || [],
                        leave_balance:
                          calendarData?.leave_balances ||
                          calendarData?.leave_balance ||
                          [],
                      }).unwrap();

                      showSuccess("Configuration saved successfully!");
                    } catch (error) {
                      console.error("Error saving configuration:", error);
                      toast.error(
                        error?.data?.message ||
                        "Failed to save configuration. Please try again.",
                      );
                    }
                  }}
                  disabled={isUpdating}
                  className={`px-6 py-2 rounded transition-colors duration-200 ${isUpdating
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-black text-white hover:bg-gray-800"
                    }`}
                >
                  {isUpdating ? "Saving..." : "Save Configuration"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Holiday Modal */}
      {showHolidayModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowHolidayModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedHoliday ? "Edit Holiday" : "Add New Holiday"}
              </h3>
              <button
                onClick={() => setShowHolidayModal(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Holiday Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Holiday Name *
                </label>
                <input
                  type="text"
                  value={holidayForm.name}
                  onChange={(e) => {
                    setHolidayForm({ ...holidayForm, name: e.target.value });
                    if (formErrors.name) {
                      setFormErrors({ ...formErrors, name: "" });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black ${formErrors.name
                      ? "border-red-500 focus:border-red-500"
                      : "border-gray-300 focus:border-black"
                    }`}
                  placeholder="Enter holiday name"
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={holidayForm.date}
                    onChange={(e) => {
                      setHolidayForm({ ...holidayForm, date: e.target.value });
                      if (formErrors.date) {
                        setFormErrors({ ...formErrors, date: "" });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black text-sm cursor-pointer ${formErrors.date
                        ? "border-red-500 focus:border-red-500"
                        : holidayForm.date
                          ? "border-gray-300 focus:border-black text-gray-900"
                          : "border-gray-300 focus:border-black text-zinc-500"
                      }`}
                    style={{
                      colorScheme: "light",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                    }}
                    onClick={(e) => {
                      e.target.showPicker && e.target.showPicker();
                    }}
                  />
                </div>
                {formErrors.date && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.date}</p>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200">
              <div>
                {selectedHoliday && canEdit && (
                  <button
                    onClick={() => {
                      handleDeleteHoliday(selectedHoliday.id);
                      setShowHolidayModal(false);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowHolidayModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveHoliday}
                  disabled={!isFormValid()}
                  className={`px-4 py-2 rounded transition-colors duration-200 ${isFormValid()
                      ? "bg-black text-white hover:bg-gray-800"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                >
                  {selectedHoliday ? "Update" : "Add Holiday"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowErrorModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Error</h3>
              </div>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSuccessModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckSquare className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Success</h3>
              </div>
              <p className="text-gray-600 mb-6">{successMessage}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Edit Modal */}
      {showUserEditModal && selectedUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancelUserEdit();
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit Leave Balance
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedUser.name} - {selectedUser.employeeId}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Casual Leave (days)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={userEditForm.casualLeave}
                    onChange={(e) =>
                      setUserEditForm({
                        ...userEditForm,
                        casualLeave: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Earned Leave (days)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={userEditForm.earnedLeave}
                    onChange={(e) =>
                      setUserEditForm({
                        ...userEditForm,
                        earnedLeave: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-sm"
                    min="0"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleCancelUserEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUserEdit}
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddUserModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Add User to Calendar
              </h3>
              <button
                onClick={() => setShowAddUserModal(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              {availableUsers.length === 0 ? (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Available Users
                  </h3>
                  <p className="text-gray-500">
                    All eligible users are already added to this calendar.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">
                    Select a user to add to the calendar. They will be given the
                    default casual leave balance.
                  </p>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {availableUsers.map((user) => (
                      <div
                        key={user.userId || user.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleAddUserToCalendar(user)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.image ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={user.image}
                                alt={user.name}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-zinc-200 flex items-center justify-center">
                                <span className="text-sm font-medium text-zinc-600">
                                  {user.name
                                    ?.split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email} • {user.employeeId}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          Default: {calendarConfig.casualLeavePerYear} casual, 0
                          earned
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowAddUserModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Details Modal (for non-CFM users) */}
      {showHolidayDetailsModal && selectedDateForDetails && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowHolidayDetailsModal(false);
              setSelectedDateHolidays([]);
              setSelectedDateForDetails(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Holidays on{" "}
                {selectedDateForDetails.toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </h3>
              <button
                onClick={() => {
                  setShowHolidayDetailsModal(false);
                  setSelectedDateHolidays([]);
                  setSelectedDateForDetails(null);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              {selectedDateHolidays.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No holidays on this date.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedDateHolidays.map((holiday) => (
                    <div
                      key={holiday.id}
                      className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg"
                    >
                      <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {holiday.name}
                        </h4>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowHolidayDetailsModal(false);
                  setSelectedDateHolidays([]);
                  setSelectedDateForDetails(null);
                }}
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
